import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
} from '../components/ui/dialog';
import axios from 'axios';
import { 
  ArrowLeft, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  Loader2,
  QrCode,
  User,
  Calendar,
  Edit2,
  Maximize2
} from 'lucide-react';
import { Toaster, toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const TransactionDetail = () => {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const { staff } = useAuth();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [showFullQR, setShowFullQR] = useState(false);

  useEffect(() => {
    fetchTransaction();
  }, [transactionId]);

  const fetchTransaction = async () => {
    try {
      const response = await axios.get(`${API}/transactions/${transactionId}`, { withCredentials: true });
      setTransaction(response.data);
      setNewStatus(response.data.status);
    } catch (error) {
      console.error('Error fetching transaction:', error);
      toast.error('Transaction not found');
      navigate('/app/transactions');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async () => {
    if (newStatus === transaction.status) return;
    
    setUpdating(true);
    try {
      const response = await axios.patch(`${API}/transactions/${transactionId}`, 
        { status: newStatus },
        { withCredentials: true }
      );
      setTransaction(response.data);
      setNewStatus(response.data.status);
      toast.success('Status updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleMarkAsConfirmed = async () => {
    setUpdating(true);
    try {
      const response = await axios.patch(`${API}/transactions/${transactionId}`, 
        { status: 'confirmed' },
        { withCredentials: true }
      );
      setTransaction(response.data);
      setNewStatus('confirmed');
      toast.success('Transaction marked as confirmed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm transaction');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'text-emerald-600 bg-emerald-50 border-emerald-200';
      case 'pending': return 'text-amber-600 bg-amber-50 border-amber-200';
      case 'failed': return 'text-red-600 bg-red-50 border-red-200';
      case 'manual_review': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-slate-600 bg-slate-50 border-slate-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return <CheckCircle2 className="w-6 h-6" />;
      case 'pending': return <Clock className="w-6 h-6" />;
      case 'failed': return <XCircle className="w-6 h-6" />;
      case 'manual_review': return <AlertTriangle className="w-6 h-6" />;
      default: return null;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canUpdateStatus = staff?.role === 'owner' || staff?.role === 'manager';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#0077B6]" />
      </div>
    );
  }

  if (!transaction) {
    return null;
  }

  return (
    <div data-testid="transaction-detail-page">
      <Toaster richColors position="top-right" />
      
      <Button
        variant="ghost"
        className="mb-6"
        onClick={() => navigate('/app/transactions')}
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Transactions
      </Button>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Header Card */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-xl font-semibold text-[#0A1628] font-mono">
                  {transaction.transaction_id}
                </h1>
                <p className="text-slate-500 mt-1">{transaction.description}</p>
              </div>
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border ${getStatusColor(transaction.status)}`}>
                {getStatusIcon(transaction.status)}
                <span className="font-medium capitalize">{transaction.status.replace('_', ' ')}</span>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <span className="text-sm text-slate-500">Amount</span>
                <div className="text-3xl font-bold text-[#0A1628] mt-1">
                  LRD {formatCurrency(transaction.amount)}
                </div>
              </div>
              <div>
                <span className="text-sm text-slate-500">Payment Network</span>
                <div className="mt-1">
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium ${
                    transaction.network === 'MTN' ? 'bg-yellow-100 text-yellow-800' : 'bg-orange-100 text-orange-800'
                  }`}>
                    {transaction.network} Mobile Money
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Details Card */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-[#0A1628] mb-4">Transaction Details</h2>
            
            <div className="space-y-4">
              <div className="flex justify-between py-3 border-b border-slate-100">
                <span className="text-slate-500">Reference</span>
                <span className="font-medium text-slate-700">{transaction.reference}</span>
              </div>
              <div className="flex justify-between py-3 border-b border-slate-100">
                <span className="text-slate-500">Created By</span>
                <span className="font-medium text-slate-700 flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-400" />
                  {transaction.created_by_name}
                </span>
              </div>
              <div className="flex justify-between py-3 border-b border-slate-100">
                <span className="text-slate-500">Created At</span>
                <span className="font-medium text-slate-700 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  {formatDate(transaction.created_at)}
                </span>
              </div>
              {transaction.updated_at && (
                <>
                  <div className="flex justify-between py-3 border-b border-slate-100">
                    <span className="text-slate-500">Last Updated By</span>
                    <span className="font-medium text-slate-700 flex items-center gap-2">
                      <Edit2 className="w-4 h-4 text-slate-400" />
                      {transaction.updated_by_name}
                    </span>
                  </div>
                  <div className="flex justify-between py-3">
                    <span className="text-slate-500">Updated At</span>
                    <span className="font-medium text-slate-700">
                      {formatDate(transaction.updated_at)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Status Update Card */}
          {canUpdateStatus && (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-[#0A1628] mb-4">Update Status</h2>
              
              {/* Quick Confirm Button for Pending Transactions */}
              {transaction.status === 'pending' && (
                <div className="mb-4">
                  <Button 
                    onClick={handleMarkAsConfirmed}
                    disabled={updating}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-12"
                    data-testid="mark-as-confirmed-btn"
                  >
                    {updating ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <CheckCircle2 className="w-5 h-5 mr-2" />
                    )}
                    Mark as Confirmed
                  </Button>
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    Click after verifying the mobile money payment
                  </p>
                </div>
              )}
              
              <div className="flex gap-4">
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="flex-1 h-11" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="manual_review">Manual Review</SelectItem>
                  </SelectContent>
                </Select>
                <Button 
                  onClick={updateStatus}
                  disabled={updating || newStatus === transaction.status}
                  className="btn-accent"
                  data-testid="update-status-btn"
                >
                  {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update'}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* QR Code Sidebar */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <QrCode className="w-5 h-5 text-[#0077B6]" />
              <h2 className="text-lg font-semibold text-[#0A1628]">Payment QR</h2>
            </div>
            
            {transaction.qr_code && (
              <div className="bg-white p-4 rounded-lg border border-slate-200 inline-block">
                <img 
                  src={`data:image/png;base64,${transaction.qr_code}`}
                  alt="Transaction QR Code"
                  className="w-48 h-48"
                  data-testid="qr-code-image"
                />
              </div>
            )}
            
            <p className="text-sm text-slate-500 mt-4 mb-4">
              Scan to view payment details
            </p>

            <Button
              onClick={() => setShowFullQR(true)}
              className="w-full bg-[#0077B6] hover:bg-[#0096C7] text-white"
              data-testid="show-payment-qr-btn"
            >
              <Maximize2 className="w-4 h-4 mr-2" />
              Show Payment QR
            </Button>
          </div>

          {/* Quick Info */}
          <div className="bg-slate-50 rounded-xl p-6">
            <h3 className="text-sm font-medium text-slate-700 mb-3">QR Contains</h3>
            <ul className="space-y-2 text-sm text-slate-600">
              <li>• Transaction ID</li>
              <li>• Amount: LRD {formatCurrency(transaction.amount)}</li>
              <li>• Reference: {transaction.reference}</li>
              <li>• Network: {transaction.network}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Full-Screen Payment QR Modal */}
      <Dialog open={showFullQR} onOpenChange={setShowFullQR}>
        <DialogContent className="sm:max-w-lg p-0 overflow-hidden">
          <div className="bg-gradient-to-br from-[#0077B6] to-[#023E8A] p-6 text-white text-center" data-testid="full-payment-qr-modal">
            <h2 className="text-xl font-bold mb-1">Payment Request</h2>
            <p className="text-blue-200 text-sm">Scan to complete payment</p>
          </div>
          
          <div className="p-6 text-center">
            {/* Transaction ID */}
            <div className="font-mono text-lg text-[#0077B6] font-bold mb-4">
              {transaction.transaction_id}
            </div>
            
            {/* QR Code */}
            {transaction.qr_code && (
              <div className="flex justify-center mb-6">
                <div className="bg-white p-4 rounded-xl border-4 border-[#0077B6]/20 shadow-lg">
                  <img 
                    src={`data:image/png;base64,${transaction.qr_code}`}
                    alt="Payment QR Code"
                    className="w-56 h-56"
                  />
                </div>
              </div>
            )}
            
            {/* Payment Amount */}
            <div className="bg-slate-50 rounded-xl p-4 mb-4">
              <span className="text-slate-500 text-sm">Amount to Pay</span>
              <div className="text-3xl font-bold text-[#0A1628]">
                LRD {formatCurrency(transaction.amount)}
              </div>
            </div>
            
            {/* Payment Details */}
            <div className="grid grid-cols-2 gap-3 text-sm mb-4">
              <div className="bg-slate-50 rounded-lg p-3 text-left">
                <span className="text-slate-500 block text-xs">Reference</span>
                <span className="font-medium text-slate-800">{transaction.reference}</span>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-left">
                <span className="text-slate-500 block text-xs">Network</span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  transaction.network === 'MTN' ? 'bg-yellow-100 text-yellow-800' : 'bg-orange-100 text-orange-800'
                }`}>
                  {transaction.network}
                </span>
              </div>
            </div>

            {/* Payment Instructions */}
            <div className="bg-blue-50 rounded-lg p-4 text-left">
              <p className="font-semibold text-blue-800 mb-2 text-sm">Payment Instructions:</p>
              <ol className="text-blue-700 space-y-1 text-sm list-decimal list-inside">
                <li>Open your {transaction.network} mobile money app</li>
                <li>Scan the QR code above</li>
                <li>Confirm the amount: LRD {formatCurrency(transaction.amount)}</li>
                <li>Complete the payment</li>
              </ol>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransactionDetail;
